import React, { useState, useCallback, useRef } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard, A11y } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { ImageGalleryProps, GalleryImage } from "./ImageGalleryTypes";
import { getGallerySx, counterSx, fallbackSx } from "./galleryStyles";
import ImageGalleryLightbox from "./ImageGalleryLightbox";
import noImage from "../../assets/images/noImage.png";
import { dt } from "../../themes/redesignTokens";

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  variant = "card",
  showCounter,
  showNavigation,
  onImageClick,
  initialIndex = 0,
  className,
  fallbackIcon,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  const isDesktop = useMediaQuery("(min-width:1201px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1200px)");
  const tier = isDesktop ? "desktop" : isTablet ? "tablet" : "phone";

  const hasMultiple = images.length > 1;
  const hasImages = images.length > 0;

  // Default: show counter + navigation when multiple images
  const shouldShowCounter =
    showCounter !== undefined ? showCounter : hasMultiple;
  const shouldShowNavigation =
    showNavigation !== undefined ? showNavigation : hasMultiple;

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex);
  }, []);

  const handleImageClick = useCallback(
    (image: GalleryImage, index: number) => {
      if (onImageClick) {
        onImageClick(image, index);
      } else {
        // Default: open lightbox
        setLightboxOpen(true);
      }
    },
    [onImageClick]
  );

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // No images: show fallback
  if (!hasImages) {
    return (
      <Box
        className={className}
        sx={getGallerySx(variant, tier)}
      >
        <Box sx={fallbackSx}>
          {fallbackIcon || (
            <img src={noImage} alt="No images available" draggable={false} />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        className={className}
        sx={getGallerySx(variant, tier)}
      >
        <Swiper
          modules={[
            ...(shouldShowNavigation ? [Navigation] : []),
            ...(hasMultiple ? [Pagination] : []),
            Keyboard,
            A11y,
          ]}
          navigation={shouldShowNavigation}
          pagination={hasMultiple ? { clickable: true } : false}
          keyboard={{ enabled: true }}
          initialSlide={initialIndex}
          onSlideChange={handleSlideChange}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
          }}
          loop={hasMultiple}
          spaceBetween={0}
          slidesPerView={1}
          style={{ width: "100%", height: "100%" }}
        >
          {images.map((image, index) => (
            <SwiperSlide key={image.id}>
              <img
                src={image.src}
                alt={image.alt || `Image ${index + 1}`}
                draggable={false}
                onClick={() => handleImageClick(image, index)}
                style={{ cursor: onImageClick || !onImageClick ? "pointer" : "default" }}
                loading="lazy"
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Counter overlay */}
        {shouldShowCounter && hasMultiple && (
          <Box sx={counterSx}>
            <Box
              component="span"
              sx={{ fontFamily: dt.fontMono, fontSize: "11px" }}
            >
              {activeIndex + 1}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: dt.font,
                fontSize: "10px",
                opacity: 0.7,
                mx: "1px",
              }}
            >
              /
            </Box>
            <Box
              component="span"
              sx={{ fontFamily: dt.fontMono, fontSize: "11px" }}
            >
              {images.length}
            </Box>
          </Box>
        )}
      </Box>

      {/* Lightbox (managed internally) */}
      <ImageGalleryLightbox
        images={images}
        open={lightboxOpen}
        onClose={handleLightboxClose}
        initialIndex={activeIndex}
      />
    </>
  );
};

export default ImageGallery;
